/**
 * 거래처 파트너 API — supplier(매입처) + customer(고객사) + contact(담당자).
 */

import type { RowsResponse } from '../types';
import { API_BASE, authHeaders, getJson, postJson, patchJson, parseResponse } from './http';
import {
  SupplierSearchResponseSchema, SupplierCreateResponseSchema,
  CustomerRowSchema, CustomerSearchResponseSchema,
  ContactResponseSchema, ContactsResponseSchema, RowsResponseSchema,
  type SupplierRow, type CustomerRow, type Contact,
} from './schemas';

// 타입 정본 = shared z.infer 파생(schemas.ts) — 손 정의 제거(2026-07-12).
// id는 z.coerce.number()가 검증+변환 담당(기존 Number(r.id) 손 정규화 대체).
export type { SupplierRow, CustomerRow, Contact };

// ─── Supplier ───────────────────────────────────────────────────────────────

/** 거래처/벤더 선택용 — 활성 supplier 만 (보통 100개 미만이라 페이지네이션 생략).
 *  ⚠️  레거시 — RfqCreateView 의 dropdown 채우기용. 신규 코드는 searchSuppliers 사용. */
export async function fetchSuppliers(): Promise<RowsResponse> {
  return getJson<RowsResponse>(
    '/api/admin/table/supplier?limit=500&sort=name&order=asc&filter[is_active]=true',
    { schema: RowsResponseSchema },
  );
}

export interface SupplierInput {
  name: string;
  country?: string | null;
  is_active?: boolean;
  notes?: string | null;
}

/** GET /api/admin/table/supplier — autocomplete 검색 (active 만, limit 10) */
export async function searchSuppliers(
  query: string,
  signal?: AbortSignal,
): Promise<SupplierRow[]> {
  const sp = new URLSearchParams({
    search: query,
    limit: '10',
    sort: 'name',
    order: 'asc',
    'filter[is_active]': 'true',
  });
  const res = await fetch(`${API_BASE}/api/admin/table/supplier?${sp.toString()}`, {
    headers: authHeaders(),
    signal,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = parseResponse(await res.json(), SupplierSearchResponseSchema, 'GET /api/admin/table/supplier');
  return data.rows;
}

/** POST /api/admin/supplier — autocomplete "신규 생성" 옵션용 */
export async function createSupplier(input: SupplierInput): Promise<SupplierRow> {
  const res = await postJson<{ row: SupplierRow } | { rows: SupplierRow[] }>(
    '/api/admin/supplier',
    input,
    SupplierCreateResponseSchema,
  );
  if ('row' in res) return res.row;
  const first = res.rows[0];
  if (!first) throw new Error('supplier 생성 응답에 row 가 없습니다');
  return first;
}

// ─── Company (거래처/매입처 통합 목록, 마이그133) ───────────────────────────
export type CompanyRoleFilter = 'all' | 'customer' | 'supplier';

export type Company = {
  id:                   string;
  name:                 string;
  role:                 '고객' | '매입처' | '고객·매입처';
  is_customer:          boolean;
  is_supplier:          boolean;
  representative_name:  string | null;
  business_no:          string | null;
  country:              string | null;
  notes:                string | null;
  is_active:            boolean;
  contact_count:        number;
  primary_contact_name: string | null;
};

export type CompanyListResponse = {
  rows:     Company[];
  total:    number;
  page:     number;
  pageSize: number;
};

export interface CompanyListQuery {
  search?:   string;
  role?:     CompanyRoleFilter;
  sort?:     string;
  order?:    'asc' | 'desc';
  page?:     number;
  pageSize?: number;
}

/** GET /api/admin/company — 거래처 통합 목록(전체/고객/매입처 필터 + 역할 배지). */
export async function fetchCompanyList(query: CompanyListQuery = {}): Promise<CompanyListResponse> {
  const sp = new URLSearchParams();
  if (query.search) sp.set('search', query.search);
  if (query.role)   sp.set('role', query.role);
  if (query.sort)   sp.set('sort', query.sort);
  if (query.order)  sp.set('order', query.order);
  sp.set('page',     String(query.page ?? 1));
  sp.set('pageSize', String(query.pageSize ?? 50));
  // 신규 엔드포인트 — 응답 스키마 검증은 안정화 후 추가(현재 타입만).
  return getJson<CompanyListResponse>(`/api/admin/company?${sp.toString()}`);
}

/** POST /api/admin/company — 거래처 통합 생성(역할 체크박스 고객/매입처 겸업 가능). */
export async function createCompany(input: {
  name:                 string;
  is_customer:          boolean;
  is_supplier:          boolean;
  business_no?:         string | null;
  representative_name?: string | null;
  address?:             string | null;
  country?:             string | null;
  notes?:               string | null;
  vendors?:             string[];
  categories?:          string[];
  conditions?:          string[];
}): Promise<Company> {
  return postJson<Company>('/api/admin/company', input);
}

/** PATCH /api/admin/company/:id — 거래처 수정(수정 폼 전체 필드). */
export async function updateCompany(id: string | number, input: {
  name:                 string;
  is_customer:          boolean;
  is_supplier:          boolean;
  business_no?:         string | null;
  representative_name?: string | null;
  address?:             string | null;
  country?:             string | null;
  notes?:               string | null;
  vendors?:             string[];
  categories?:          string[];
  conditions?:          string[];
}): Promise<Company> {
  return patchJson<Company>(`/api/admin/company/${encodeURIComponent(String(id))}`, input);
}

/** PATCH /api/admin/company/:id/memo — 메모만 갱신(연필 빠른 편집). */
export async function updateCompanyMemo(id: string | number, memo: string): Promise<void> {
  await patchJson<{ ok: boolean }>(`/api/admin/company/${encodeURIComponent(String(id))}/memo`, { memo });
}

/** DELETE /api/admin/company/:id — 비활성화(soft delete). */
export async function deactivateCompany(id: string | number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/company/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

/** POST /api/admin/customer/:id/confirm — draft(Agent 임시) 거래처 정식 승격(컨펌). */
export async function confirmCustomerDraft(id: string | number): Promise<void> {
  await postJson<unknown>(`/api/admin/customer/${encodeURIComponent(String(id))}/confirm`, {});
}

/** POST /api/admin/contact/:id/confirm — draft(Agent 임시) 담당자 정식 승격(컨펌). */
export async function confirmContactDraft(id: string | number): Promise<void> {
  await postJson<unknown>(`/api/admin/contact/${encodeURIComponent(String(id))}/confirm`, {});
}

// ─── Company 상세 (읽기전용 모달) ──────────────────────────────────────────
export type CompanyDetail = {
  id:                  string;
  name:                string;
  role:                '고객' | '매입처' | '고객·매입처';
  is_customer:         boolean;
  is_supplier:         boolean;
  representative_name: string | null;
  business_no:         string | null;
  country:             string | null;
  address:             string | null;
  notes:               string | null;
  is_active:           boolean;
  vendors:             string[];
  categories:          string[];
  conditions:          string[];
  deal_count:          number;
  rfq_request_count:   number;
  rfq_priced_count:    number;
  contacts:            CompanyContact[];
};

/** GET /api/admin/company/:id — 거래처 상세(공통 + 역할별 정보 + 담당자). */
export async function fetchCompanyDetail(id: string | number): Promise<CompanyDetail> {
  return getJson<CompanyDetail>(`/api/admin/company/${encodeURIComponent(String(id))}`);
}

// ─── Company 담당자(contact) 통합 목록/생성 ────────────────────────────────
export type CompanyContact = {
  id:                  string;
  customer_id:         string | null;
  name:                string;
  position:            string | null;
  affiliation:         string | null;
  email:               string | null;
  phone:               string | null;
  office_phone:        string | null;
  notes:               string | null;
  is_customer_contact: boolean;
  is_supplier_contact: boolean;
  role:                '고객 담당자' | '매입처 담당자' | '고객·매입처 담당자' | '-';
  company_name:        string | null;
  is_active:           boolean;
};

export type CompanyContactListResponse = {
  rows:     CompanyContact[];
  total:    number;
  page:     number;
  pageSize: number;
};

export interface CompanyContactListQuery {
  search?:   string;
  role?:     CompanyRoleFilter;   // all | customer | supplier (담당자 역할)
  page?:     number;
  pageSize?: number;
}

/** GET /api/admin/company/contacts — 담당자 통합 목록(역할 필터 + 소속 회사명). */
export async function fetchCompanyContacts(
  query: CompanyContactListQuery = {},
): Promise<CompanyContactListResponse> {
  const sp = new URLSearchParams();
  if (query.search) sp.set('search', query.search);
  if (query.role)   sp.set('role', query.role);
  sp.set('page',     String(query.page ?? 1));
  sp.set('pageSize', String(query.pageSize ?? 50));
  return getJson<CompanyContactListResponse>(`/api/admin/company/contacts?${sp.toString()}`);
}

/** POST /api/admin/company/contacts — 담당자 생성(소속 회사 지정, 역할은 회사서 파생). */
export async function createCompanyContact(input: {
  customer_id:  number;
  name:         string;
  position?:    string | null;
  affiliation?: string | null;
  email?:       string | null;
  phone?:       string | null;
  office_phone?: string | null;
  memo?:        string | null;
}): Promise<CompanyContact> {
  return postJson<CompanyContact>('/api/admin/company/contacts', input);
}

/** PATCH /api/admin/company/contacts/:id — 담당자 수정(소속 변경 시 역할 재파생). */
export async function updateCompanyContact(id: string | number, input: {
  customer_id:  number;
  name:         string;
  position?:    string | null;
  affiliation?: string | null;
  email?:       string | null;
  phone?:       string | null;
  office_phone?: string | null;
  memo?:        string | null;
}): Promise<CompanyContact> {
  return patchJson<CompanyContact>(`/api/admin/company/contacts/${encodeURIComponent(String(id))}`, input);
}

/** PATCH /api/admin/company/contacts/:id/memo — 담당자 메모만 갱신(연필). */
export async function updateCompanyContactMemo(id: string | number, memo: string): Promise<void> {
  await patchJson<{ ok: boolean }>(`/api/admin/company/contacts/${encodeURIComponent(String(id))}/memo`, { memo });
}

/** DELETE /api/admin/company/contacts/:id — 담당자 삭제(soft delete). */
export async function deactivateCompanyContact(id: string | number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/admin/company/contacts/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// ─── Customer (Sprint 2) ───────────────────────────────────────────────────

// CustomerRow — 관문 z.infer 정본 (위 re-export 참조).

export interface CustomerInput {
  name: string;
  business_no?: string | null;
  representative_name?: string | null;
  representative_phone?: string | null;
  representative_email?: string | null;
  address?: string | null;
  industry?: string | null;
  homepage_url?: string | null;
  is_active?: boolean;
  notes?: string | null;
  created_by?: string | null;
}

/** GET /api/admin/customer — autocomplete 용 검색 (active 만). */
export async function fetchCustomers(
  query: string,
  signal?: AbortSignal,
): Promise<CustomerRow[]> {
  const sp = new URLSearchParams({
    search: query,
    pageSize: '10',
    sort: 'name',
    order: 'asc',
    is_active: 'true',
  });
  const res = await fetch(`${API_BASE}/api/admin/customer?${sp.toString()}`, {
    headers: authHeaders(),
    signal,
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  // id string→number 정규화는 스키마의 z.coerce.number()가 담당
  const data = parseResponse(await res.json(), CustomerSearchResponseSchema, 'GET /api/admin/customer');
  return data.rows;
}

/** POST /api/admin/customer — autocomplete 의 "신규 생성" 옵션용. Customer 직접 반환. */
export async function createCustomer(input: CustomerInput): Promise<CustomerRow> {
  return postJson<CustomerRow>('/api/admin/customer', input, CustomerRowSchema);
}

/** PATCH /api/admin/customer/:id — 거래처 정보 수정(상호/사업자번호 등). */
export async function updateCustomer(
  id: string | number,
  patch: Partial<CustomerInput>,
): Promise<CustomerRow> {
  return patchJson<CustomerRow>(`/api/admin/customer/${encodeURIComponent(String(id))}`, patch, CustomerRowSchema);
}

// ─── Contact (거래처 담당자) ─────────────────────────────────────────

/** PATCH /api/admin/contact/:id — 거래처 담당자 이름/직책/이메일/전화 수정. */
export interface ContactPatch {
  name?:     string;
  position?: string | null;
  email?:    string | null;
  phone?:    string | null;
}
export async function updateContact(
  id: string | number,
  patch: ContactPatch,
  dealId?: string | number | null,   // 딜 상세에서 편집 시 그 딜 히스토리에 기록
): Promise<unknown> {
  const q = dealId != null && dealId !== '' ? `?deal_id=${encodeURIComponent(String(dealId))}` : '';
  return patchJson<unknown>(`/api/admin/contact/${encodeURIComponent(String(id))}${q}`, patch);
}

// Contact — 관문 z.infer 정본 (위 re-export 참조).

/** GET /api/admin/contact?customer_id=X — 특정 거래처의 활성 담당자 목록. */
export async function fetchContacts(customerId: string | number): Promise<Contact[]> {
  const r = await getJson<{ rows: Contact[] }>(
    `/api/admin/contact?customer_id=${encodeURIComponent(String(customerId))}&is_active=true&pageSize=200&sort=name&order=asc`,
    { schema: ContactsResponseSchema },
  );
  return r.rows;
}

/** POST /api/admin/contact — 신규 거래처 담당자 등록. */
export async function createContact(input: {
  customer_id: number;
  name:        string;
  position?:   string | null;
  email?:      string | null;
  phone?:      string | null;
}): Promise<Contact> {
  return postJson<Contact>('/api/admin/contact', input, ContactResponseSchema);
}
